import {
	ATTRIBUTES,
	ON_CHANGE_DEBOUNCE_TIMER,
	PUB_SUB_EVENTS,
	SELECTORS,
} from '@/scripts/core/global'
import {
	debounce,
	fetchConfig,
	getAttributeOrThrow,
	qsaOptional,
	qsOptional,
	qsRequired,
	targetRequired,
} from '@/scripts/core/global'
import { publish, PubSubEvent, subscribe } from '@/scripts/core/global'
import { routes, type uCoastWindow } from '@/scripts/setup'
import { type ShopifySectionRenderingSchema } from '@/scripts/types/theme'
import { trapFocus } from '@/scripts/core/global'
import { type CartDrawer } from '@/scripts/cart/cart-drawer'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { enqueueDebouncedCartChange } from '@/scripts/cart/cart-queue'

declare let window: uCoastWindow

export class CartItems extends UcoastEl {
	// static
	static htmlSelector = 'cart-items'
	static selectors = {
		element: 'cart-items',
		itemLink: '[data-uc-cart-item-name]',
		lineItemStatus: '[data-uc-cart-status]',
		errors: '[data-uc-cart-errors]',
		liveRegionText: '#CartPage-LiveRegionText',
		main: '#main-cart-items',
		cartDrawerInner: '[data-uc-cart-drawer-inner]',
		cartDrawerInnerEmpty: '[data-uc-cart-drawer-inner-empty]',
		item: '[data-uc-cart-item]',
		itemErrorText: '[data-uc-cart-item-error-text]',
		footer: '#CartPage-Footer',
		loadingOverlay: '[data-uc-loading-overlay]',
		// the following selectors are partial - they will be concatenated with the line ID
		line: '#CartPage-Item', // ex: `${this.instanceSelectors.line}-${line}`
		lineQuantity: '#CartPage-LineItemQuantity', // ex: `${this.instanceSelectors.lineQuantity}-${line}`
		lineError: '#CartPage-LineItemError', // ex: `${this.instanceSelectors.lineError}-${line}`
	}
	// TODO: refactor to global attributes selector
	static attributes = {
		disabled: 'data-uc-disabled',
	}

	// instance specific property types
	instanceSelectors = CartItems.selectors
	lineItemStatusElement: HTMLElement
	cartUpdateUnsubscriber?: () => void = undefined

	// init
	constructor() {
		super()
		this.setInstanceSelectors()
		this.lineItemStatusElement = qsRequired(
			this.instanceSelectors.lineItemStatus
		)

		const debouncedOnChange = debounce((event: Event) => {
			this.onChange(event)
		}, ON_CHANGE_DEBOUNCE_TIMER)

		this.addEventListener('change', debouncedOnChange.bind(this))
	}

	// have to instantiate selectors here so they can be different for CartDrawerItems
	setInstanceSelectors() {
		this.instanceSelectors = CartItems.selectors
	}

	// from here on is default Dawn CartItems -> typescript
	override connectedCallback() {
		this.cartUpdateUnsubscriber = subscribe(
			PUB_SUB_EVENTS.cartUpdate,
			(pubSubEvent: PubSubEvent) => {
				if (
					pubSubEvent &&
					'source' in pubSubEvent &&
					pubSubEvent.source === 'cart-items'
				) {
					return
				}
			}
		)
	}

	override disconnectedCallback() {
		if (this.cartUpdateUnsubscriber) {
			this.cartUpdateUnsubscriber()
		}
	}

	protected isCartEmpty(parsedState: { item_count: number }): boolean {
		return parsedState.item_count === 0
	}

	onChange(event: Event) {
		const target = targetRequired<Event, HTMLInputElement>(event)
		const targetIndex = getAttributeOrThrow('data-index', target)
		const lineItemKey = getAttributeOrThrow('data-line-item-key', target)
		const activeElement = document.activeElement as HTMLElement
		if (!activeElement) throw new Error('no document.activeElement')
		const activeElementName = activeElement?.getAttribute('name') ?? 'updates[]'
		this.updateQuantity(targetIndex, target.value, activeElementName, lineItemKey)
	}


	getSectionsToRender(): ShopifySectionRenderingSchema[] {
		return [
			{
				id: 'main-cart-items',
				section: getAttributeOrThrow(
					'data-id',
					qsRequired(this.instanceSelectors.main)
				),
				selector: '.js-contents',
			},
			{
				id: 'CartIconBubble',
				section: 'cart-icon-bubble',
				selector: SELECTORS.cartLink,
			},
			{
				id: 'CartPage-LiveRegionText',
				section: 'cart-live-region-text',
				selector: '.shopify-section',
			},
			{
				id: 'CartPage-Footer',
				section: getAttributeOrThrow(
					'data-id',
					qsRequired(this.instanceSelectors.footer)
				),
				selector: '.js-contents',
			},
		]
	}

	updateQuantity(line: string, quantity: string, name?: string, lineItemKey?: string) {
		// Apply optimistic DOM updates immediately
		this.applyOptimisticUpdate(line, quantity)

		const key = lineItemKey ?? line

		enqueueDebouncedCartChange(
			key,
			line,
			quantity,
			lineItemKey,
			(debouncedLine, debouncedQuantity, debouncedKey) =>
				this.executeCartChange(debouncedLine, debouncedQuantity, name, debouncedKey),
		)
	}

	/**
	 * Apply immediate visual updates before the server responds.
	 * Quantities and item count update instantly; prices are dimmed.
	 */
	private applyOptimisticUpdate(line: string, quantity: string) {
		const qty = parseInt(quantity)
		const lineItem = qsOptional(`${this.instanceSelectors.line}-${line}`)

		if (lineItem) {
			if (qty === 0) {
				// Optimistic removal — CSS animation handles fade + collapse
				lineItem.classList.add('cart-line--removing')
			} else {
				// Update visible quantity text
				const qtyValue = qsOptional('[data-cart-quantity-value]', lineItem)
				if (qtyValue) qtyValue.textContent = quantity

				// Dim the line price while syncing
				lineItem.classList.add('cart-line--syncing')
			}
		}

		// Update header item count
		const itemCountEl = qsOptional('[data-item-count]')
		if (itemCountEl) {
			const currentCount = parseInt(itemCountEl.textContent?.replace(/\D/g, '') ?? '0')
			const prevQty = lineItem
				? parseInt(lineItem.getAttribute('data-uc-cart-quantity') ?? '1')
				: 0
			const diff = qty - prevQty
			const newCount = Math.max(0, currentCount + diff)
			itemCountEl.textContent = `(${newCount})`
		}

		// Update cart icon bubble count
		const bubble = qsOptional('[data-uc-cart-icon-bubble]')
		if (bubble) {
			const countEl = qsOptional('.cart-count-bubble span[aria-hidden]', bubble)
			if (countEl) {
				const currentCount = parseInt(countEl.textContent ?? '0')
				const prevQty = lineItem
					? parseInt(lineItem.getAttribute('data-uc-cart-quantity') ?? '1')
					: 0
				const diff = qty - prevQty
				const newCount = Math.max(0, currentCount + diff)
				countEl.textContent = `${newCount}`
			}
		}
	}

	/**
	 * Execute the actual /cart/change fetch. Called by the debounce system
	 * after the debounce timer fires, serialized through the cart queue.
	 */
	private executeCartChange(
		line: string,
		quantity: string,
		name: string | undefined,
		lineItemKey: string | undefined,
	): Promise<void> {
		const sectionIds = [...new Set(this.getSectionsToRender().map(
			(section) => section.section
		))]
		const cartChangePayload: Record<string, unknown> = {
			quantity,
			sections: sectionIds,
			sections_url: window.location.pathname,
		}
		if (lineItemKey) {
			cartChangePayload.id = lineItemKey
		} else {
			cartChangePayload.line = line
		}
		const body = JSON.stringify(cartChangePayload)

		return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
			.then((response) => response.text())
			.then((state) => {
				const parsedState = JSON.parse(state)
				const quantityElement = qsOptional<HTMLInputElement>(
					`${this.instanceSelectors.lineQuantity}-${line}`
				)
				const items = document.querySelectorAll(
					this.instanceSelectors.item
				)

				if (parsedState.errors || parsedState.status) {
					if (quantityElement) {
						quantityElement.value = getAttributeOrThrow(
							'value',
							quantityElement
						)
					}
					this.updateLiveRegions(line, parsedState.errors || parsedState.description || window.cartStrings.error)
					return
				}

				const cartDrawerWrapper = qsOptional<CartDrawer>('cart-drawer')
				const cartFooter = qsOptional(this.instanceSelectors.footer)
				if (this.isCartEmpty(parsedState)) {
					this.setAttribute(ATTRIBUTES.cartEmpty, '')
					cartFooter &&
						cartFooter.setAttribute(ATTRIBUTES.cartEmpty, '')
					cartDrawerWrapper &&
						cartDrawerWrapper.setAttribute(ATTRIBUTES.cartEmpty, '')
				} else {
					this.removeAttribute(ATTRIBUTES.cartEmpty)
					cartFooter &&
						cartFooter.removeAttribute(ATTRIBUTES.cartEmpty)
					cartDrawerWrapper &&
						cartDrawerWrapper.removeAttribute(ATTRIBUTES.cartEmpty)
				}

				this.getSectionsToRender().forEach(
					(section: ShopifySectionRenderingSchema) => {
						const elementToReplace = section.selector
							? qsRequired(section.selector)
							: qsRequired(`#${section.id}`)
						if (!section.section)
							throw new Error('no section.section')
						if (!section.selector)
							throw new Error('no section.selector')
						elementToReplace.innerHTML = this.getSectionInnerHTML(
							parsedState.sections[section.section],
							section.selector
						)
					}
				)
				const updatedItem = lineItemKey
					? parsedState.items.find((item: { key: string }) => item.key === lineItemKey)
					: parsedState.items[parseInt(line) - 1]
				const updatedValue = updatedItem ? updatedItem.quantity : undefined
				let message = ''
				if (
					quantityElement &&
					items.length === parsedState.items.length &&
					updatedValue !== parseInt(quantityElement.value)
				) {
					if (typeof updatedValue === 'undefined') {
						message = window.cartStrings.error
					} else {
						message = window.cartStrings.quantityError.replace(
							'[quantity]',
							updatedValue
						)
					}
				}
				this.updateLiveRegions(line, message)

				const lineItem = qsOptional(
					`${this.instanceSelectors.line}-${line}`
				)
				if (lineItem?.querySelector(`[name="${name}"]`)) {
					cartDrawerWrapper
						? trapFocus(
								cartDrawerWrapper,
								qsRequired(`[name="${name}"]`, lineItem)
							)
						: qsRequired(`[name="${name}"]`, lineItem).focus()
				} else if (this.isCartEmpty(parsedState) && cartDrawerWrapper) {
					trapFocus(
						qsRequired(
							this.instanceSelectors.cartDrawerInnerEmpty,
							cartDrawerWrapper
						),
						qsRequired('a', cartDrawerWrapper)
					)
				} else if (
					document.querySelector(this.instanceSelectors.item) &&
					cartDrawerWrapper
				) {
					trapFocus(
						cartDrawerWrapper,
						qsRequired(this.instanceSelectors.itemLink)
					)
				}
				publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' })
				if (cartDrawerWrapper) {
					void window.Ucoast.mediaManager.loadAllInContainer(cartDrawerWrapper)
				}

				cartDrawerWrapper?.setCloseButtonListener()
			})
			.catch((error) => {
				console.error(error)
				this.querySelectorAll(
					this.instanceSelectors.loadingOverlay
				).forEach((overlay) => overlay.classList.add('hidden'))
				const errors = qsOptional(this.instanceSelectors.errors)
				if (errors) errors.textContent = window.cartStrings.error
			})
	}

	updateLiveRegions(line: string, message: string) {
		const lineItemError = qsOptional(
			`${this.instanceSelectors.lineError}-${line}`
		)
		if (lineItemError) {
			const errorText = qsRequired(
				this.instanceSelectors.itemErrorText,
				lineItemError
			)
			errorText.innerHTML = message
		}

		this.lineItemStatusElement.setAttribute('aria-hidden', 'true')

		const cartStatus = qsRequired(this.instanceSelectors.liveRegionText)
		cartStatus.setAttribute('aria-hidden', 'false')

		setTimeout(() => {
			cartStatus.setAttribute('aria-hidden', 'true')
		}, 1000)
	}

	getSectionInnerHTML(html: string, selector: string) {
		const newDocument = new DOMParser().parseFromString(html, 'text/html')
		const newElement = qsRequired(selector, newDocument.documentElement)
		return newElement.innerHTML
	}

	enableLoading(line: string) {
		const mainCartItems = this.getMainCartItems()
		mainCartItems.setAttribute(CartItems.attributes.disabled, '')

		this.getCartItemElements(line)?.forEach((overlay) =>
			overlay.classList.remove('hidden')
		)

		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}

		this.lineItemStatusElement.setAttribute('aria-hidden', 'false')
	}

	getMainCartItems() {
		return qsRequired(this.instanceSelectors.main)
	}

	getCartItemElements(line: string) {
		return qsaOptional(
			`${this.instanceSelectors.line}-${line} ${this.instanceSelectors.loadingOverlay}`,
			this
		)
	}

	disableLoading(line: string) {
		this.getMainCartItems().removeAttribute(CartItems.attributes.disabled)
		this.getCartItemElements(line)?.forEach((overlay) =>
			overlay.classList.add('hidden')
		)
	}
}
